import { useInfiniteQuery } from '@tanstack/react-query';

import { fetchCursorGroupedNotifications, fetchCursorNotifications } from '../services';
import type {
  GroupedNotification,
  NotificationStatusFilter,
  PaginatedGroupedNotificationsResponse,
} from '../types';

type UseCursorNotificationListParams = {
  limit: number;
  status: Extract<NotificationStatusFilter, 'all' | 'unread'>;
  grouped?: boolean;
};

export function useCursorNotificationList({
  limit,
  status,
  grouped = false,
}: UseCursorNotificationListParams) {
  return useInfiniteQuery({
    queryKey: ['notifications', 'cursor-list', limit, status, grouped],
    initialPageParam: null as string | null,
    queryFn: async ({ pageParam }): Promise<PaginatedGroupedNotificationsResponse> => {
      const params = {
        limit,
        cursor: pageParam,
        is_read: status === 'unread' ? false : undefined,
      };

      if (grouped) {
        return fetchCursorGroupedNotifications(params);
      }

      const flat = await fetchCursorNotifications(params);
      return {
        items: flat.items.map(
          (n): GroupedNotification => ({
            group_id: n.group_id ?? n.id,
            type: n.type,
            title: n.title,
            agent_id: n.agent_id,
            count: 1,
            unread_count: n.is_read ? 0 : 1,
            is_read: n.is_read,
            latest_at: n.created_at,
            deep_link: null,
            notifications: [n],
          })
        ),
        pagination: flat.pagination,
      };
    },
    getNextPageParam: lastPage => {
      if (lastPage.pagination.type !== 'cursor') {
        return undefined;
      }

      const pagination = lastPage.pagination;
      return pagination.has_more ? pagination.next_cursor : undefined;
    },
    select: data => ({
      ...data,
      groups: data.pages.flatMap(page => page.items),
    }),
  });
}

export type { GroupedNotification };
